import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import logo from "../assets/vimbal_logo_h_earth.svg";
import { colors, fonts, media } from "../styles/theme";
import { nav, footerNav, links, award, nameMeaning, contact } from "../pages/landingContent";
import { verticals } from "../pages/verticalContent";
import { Container } from "./landing/ui";

const FooterContainer = styled.footer`
  background: ${colors.sand};
  border-top: 1px solid ${colors.stone};
  padding: 72px 0 32px;
`;

const Top = styled.div`
  display: grid;
  gap: 40px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.3fr) repeat(3, minmax(0, 0.7fr));
  }
`;

const Brand = styled.div`
  img {
    height: 44px;
    width: auto;
  }
  p {
    margin-top: 18px;
    max-width: 380px;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: 24px;
    line-height: 1.25;
    letter-spacing: -0.015em;
    color: ${colors.ink};
  }
`;

const FullName = styled.span`
  display: block;
  margin-top: 10px;
  font-family: ${fonts.mono};
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.pebble};
`;

const AwardLink = styled.a`
  display: inline-block;
  margin-top: 16px;
  font-family: ${fonts.mono};
  font-size: 12px;
  letter-spacing: 0.06em;
  line-height: 1.6;
  color: ${colors.copper};

  &:hover {
    text-decoration: underline;
  }
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  h2 {
    font-family: ${fonts.mono};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.pebble};
    margin-bottom: 6px;
  }
`;

const FooterLink = styled(Link)`
  font-family: ${fonts.body};
  font-size: 17px;
  color: ${colors.bark};
  width: fit-content;
  transition: color 0.25s ease;

  small {
    font-size: 13px;
    color: ${colors.pebble};
  }
  &:hover {
    color: ${colors.copper};
  }
`;

const Bottom = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  margin-top: 64px;
  padding-top: 24px;
  border-top: 1px solid ${colors.stone};
  font-family: ${fonts.body};
  font-size: 14px;
  color: ${colors.pebble};
`;

const BackToTop = styled.button`
  background: none;
  cursor: pointer;
  font: inherit;
  color: ${colors.bark};

  &:hover {
    color: ${colors.copper};
  }
`;

const Footer = () => (
  <FooterContainer>
    <Container>
      <Top>
        <Brand>
          <img src={logo} alt="vimbal" />
          <p>The non-invasive AI brain–computer interface for calmer, faster minds.</p>
          <FullName>{nameMeaning.full}</FullName>
          <AwardLink href={award.href} target="_blank" rel="noopener noreferrer">
            ★ {award.title} · {award.region} · {award.source}
          </AwardLink>
        </Brand>
        <Col>
          <h2>Who it's for</h2>
          {verticals.map((v) => (
            <FooterLink key={v.slug} to={`/${v.slug}`}>
              {v.nav}
            </FooterLink>
          ))}
        </Col>
        <Col>
          <h2>Explore</h2>
          <FooterLink to="/">Overview</FooterLink>
          {[...nav, ...footerNav].map((item) => (
            <FooterLink key={item.to} to={item.to}>
              {item.label}
            </FooterLink>
          ))}
        </Col>
        <Col>
          <h2>Contact</h2>
          <FooterLink to={links.register}>Register interest</FooterLink>
          <FooterLink as="a" href={`mailto:${contact.email}`}>
            {contact.email}
          </FooterLink>
          <FooterLink as="a" href={contact.phoneHref}>
            {contact.phone} <small>({contact.phoneNote})</small>
          </FooterLink>
          <FooterLink as="a" href={contact.linkedin} target="_blank" rel="noopener noreferrer">
            {contact.linkedinLabel} ↗
          </FooterLink>
        </Col>
      </Top>
      <Bottom>
        <span>© {new Date().getFullYear()} Vimbal Inc. vimbal™ · patent filed in 13+ jurisdictions.</span>
        <BackToTop type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          Back to top ↑
        </BackToTop>
      </Bottom>
    </Container>
  </FooterContainer>
);

export default Footer;
